import { useState, useEffect, useCallback } from "react";

const isIOSDevice = () =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;

const isStandaloneMode = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

// Shared install-to-homescreen/desktop logic for both the mobile (/m/*) and
// desktop apps. Chromium browsers fire `beforeinstallprompt` which we defer
// and replay on demand; Safari/iOS never fires it, so callers fall back to
// showing manual "Add to Home Screen" instructions when `isIOS` is true.
export function useInstallPrompt() {
  const [deferredEvent, setDeferredEvent] = useState(null);
  const [isStandalone, setIsStandalone] = useState(isStandaloneMode());

  useEffect(() => {
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredEvent(e);
    };
    const handleInstalled = () => {
      setDeferredEvent(null);
      setIsStandalone(true);
    };
    window.addEventListener("beforeinstallprompt", handleBeforeInstall);
    window.addEventListener("appinstalled", handleInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
      window.removeEventListener("appinstalled", handleInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredEvent) return false;
    deferredEvent.prompt();
    const { outcome } = await deferredEvent.userChoice;
    setDeferredEvent(null);
    return outcome === "accepted";
  }, [deferredEvent]);

  return {
    canInstall: !!deferredEvent && !isStandalone,
    promptInstall,
    isStandalone,
    isIOS: isIOSDevice(),
  };
}
